// eslint-disable-next-line max-lines-per-function
import { noop } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { ReactElement, useMemo } from 'react';

import {
   GynecologicalInterview,
   GynecologicalInterviewQuery,
   MedicalEvent,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering } from '@dreryk/react-common';
import {
   colorsMatrix,
   createDynamicColumnsWidth,
   LinkSize,
   TableColumnBackgroundColorResolver,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientMedicalInformationRemoveModalOpener } from '~libs.desktop.patient-medical-information';

import { AttachmentsLinks } from '../../shared/AttachmentLinks/AttachmentsLinks';
import { patientMedicalInformationTranslationKeys } from '../../translations';
import { refetchAllMedicalInformation } from '../shared/refetchAllMedicalInformation';
import { DescriptionCell } from '../shared/table/DescriptionCell';
import { MedicalInformationModalOpenLink } from '../shared/table/MedicalInformationModalOpenLink';
import { MedicalInformationRemoveLink } from '../shared/table/MedicalInformationRemoveLink';
import { useMedicalInformationRemoveEntryAvailable } from '../shared/useMedicalInformationRemoveEntryAvailable';
import { useShowRemovedMedicalInformationState } from '../MedicalInformationShowRemovedCheckbox.hooks';

import {
   isPatientPregnantInterview,
   isPregnancyCompletionInterview,
   isRegularGynecologicalInterview,
} from './gynecologicalInterviewModal/GynecologicalInterviewModal';
import { GynecologicalInterviewModalOpener } from './GynecologicalInterviewModalOpener';
import { MedicalEventCell } from './MedicalEventCell';
import { GynecologicalInterviewPatientPregnantEntryCell } from './tableCells/GynecologicalInterviewPatientPregnantEntryCell';
import { GynecologicalInterviewPregnancyCompletionEntryCell } from './tableCells/GynecologicalInterviewPregnancyCompletionEntryCell';
import { GynecologicalInterviewRegularInterviewEntryCell } from './tableCells/GynecologicalInterviewRegularInterviewEntryCell';

const renderEntryCell = (gynecologicalInterview: GynecologicalInterview): JSX.Element => {
   if (isPatientPregnantInterview(gynecologicalInterview)) {
      return <GynecologicalInterviewPatientPregnantEntryCell patientPregnantInterview={gynecologicalInterview} />;
   }
   if (isPregnancyCompletionInterview(gynecologicalInterview)) {
      return (
         <GynecologicalInterviewPregnancyCompletionEntryCell pregnancyCompletionInterview={gynecologicalInterview} />
      );
   }
   return <GynecologicalInterviewRegularInterviewEntryCell />;
};

// eslint-disable-next-line max-lines-per-function
export const useGynecologicalInterviewTableColumns = (
   onRemoveSuccess: VoidFunction
): TableColumnConfiguration<GynecologicalInterview>[] => {
   const medicalInformationRemoveEntryAvailable = useMedicalInformationRemoveEntryAvailable();

   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         creationDate: '115px',
         author: '200px',
         entry: '50%',
         description: '50%',
         medicalEvent: '40px',
         attachments: '276px',
         options: '56px',
      });

      const getBackgroundColor: TableColumnBackgroundColorResolver<GynecologicalInterview> = (props) =>
         props.bean.medicalInformationRemoved ? colorsMatrix.red.lighter : undefined;

      return [
         {
            accessor: 'creationDate',
            label: patientMedicalInformationTranslationKeys.gynecologicalInterview.table.entryDate,
            width: columnsWidth.creationDate,
            formatter: dateFormatter,
            backgroundColor: getBackgroundColor,
         },
         {
            name: 'author',
            accessor: (medicalInformation) => medicalInformation.createdBy,
            label: patientMedicalInformationTranslationKeys.gynecologicalInterview.table.enteredBy,
            width: columnsWidth.author,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<GynecologicalInterview>) => (
               <PractitionerSafePresenter
                  testId={'author'}
                  practitioner={props.bean.createdBy}
                  type={'standalone'}
                  withIdentification
               />
            ),
         },
         {
            name: 'entry',
            accessor: (bean) => bean,
            label: patientMedicalInformationTranslationKeys.gynecologicalInterview.table.entry,
            width: columnsWidth.entry,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<GynecologicalInterview, string>): ReactElement | null =>
               renderEntryCell(props.bean),
         },
         {
            accessor: 'description',
            label: patientMedicalInformationTranslationKeys.gynecologicalInterview.table.description,
            width: columnsWidth.description,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<GynecologicalInterview, string>): ReactElement | null => (
               <DescriptionCell description={props.bean.description} testId={props.bean.id} />
            ),
         },
         {
            name: 'medicalEvent',
            accessor: (gynecologicalInterview: GynecologicalInterview) => gynecologicalInterview,
            label: patientMedicalInformationTranslationKeys.gynecologicalInterview.table.medicalEvent,
            width: columnsWidth.medicalEvent,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<GynecologicalInterview, MedicalEvent>): ReactElement | null =>
               isRegularGynecologicalInterview(props.bean) ? null : (
                  <MedicalEventCell medicalEvent={props.bean.medicalEvent} testId={props.bean.id} />
               ),
         },
         {
            name: 'attachments',
            accessor: (medicalInformation) => medicalInformation.attachments,
            label: patientMedicalInformationTranslationKeys.shared.table.attachments,
            width: columnsWidth.attachments,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<GynecologicalInterview>): ReactElement | null =>
               isEmpty(props.bean.attachments) ? null : (
                  <GynecologicalInterviewModalOpener
                     gynecologicalInterview={props.bean}
                     onMedicalDataAccessAuthorizationUpdate={refetchAllMedicalInformation}
                  >
                     {({ openModal }) => (
                        <AttachmentsLinks
                           attachments={props.bean.attachments!}
                           onShowMoreAttachmentsClick={openModal}
                        />
                     )}
                  </GynecologicalInterviewModalOpener>
               ),
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: noop,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<GynecologicalInterview>): ReactElement => (
               <>
                  <GynecologicalInterviewModalOpener
                     gynecologicalInterview={props.bean}
                     onMedicalDataAccessAuthorizationUpdate={refetchAllMedicalInformation}
                  >
                     {({ openModal }) => (
                        <MedicalInformationModalOpenLink
                           onClick={openModal}
                           unsignedAttachmentCount={props.bean.unsignedAttachmentCount}
                           size={LinkSize.BIG}
                        />
                     )}
                  </GynecologicalInterviewModalOpener>

                  {medicalInformationRemoveEntryAvailable(
                     props.bean.createdBy,
                     props.bean.medicalInformationRemoved
                  ) ? (
                     <PatientMedicalInformationRemoveModalOpener
                        medicalInformationId={props.bean.id}
                        onRemoveSuccess={onRemoveSuccess}
                     >
                        {({ openModal }) => <MedicalInformationRemoveLink onClick={openModal} />}
                     </PatientMedicalInformationRemoveModalOpener>
                  ) : null}
               </>
            ),
         },
      ];
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [medicalInformationRemoveEntryAvailable]);
};

const DEFAULT_ORDERINGS: Ordering<GynecologicalInterview>[] = [
   {
      descending: true,
      property: 'creationDate',
   },
];

export const useGynecologicalInterviewLazyLoadedDataConfiguration = (
   patientId: number
): LazyLoadedDataConfigurationByCqrsQuery<GynecologicalInterview> => {
   const [showRemoved] = useShowRemovedMedicalInformationState();

   return useLazyLoadedDataConfigurationByCqrsQuery<GynecologicalInterview>(() => {
      const query = new GynecologicalInterviewQuery();
      query.patientId = Slot.emptyIfNull(patientId);
      query.showRemoved = showRemoved;
      return query;
   }, DEFAULT_ORDERINGS);
};
